import { SortDir } from '__generated__';
import moment from 'moment-timezone';

import { SortDirection } from 'shared/components/data/Sort';

type TimestampCacheType = Map<string, moment.Moment>;
type ComparisonType = string | number | moment.Duration | null;

export interface PropMapType {
  [key: string]: 'duration' | 'number' | 'string' | 'timestamp';
}

// complexity 12
// eslint-disable-next-line complexity
function getComparison(
  left: ComparisonType,
  right: ComparisonType,
  prop: string,
  propMap: PropMapType,
  timestampCache: TimestampCacheType
) {
  if (left === right) {
    return 0;
  }

  // It is possible for values to be null. Order them last when in ascending
  // order and first otherwise.
  if (left === null || right === null) {
    return left === null ? 1 : -1;
  }

  // Use the prop to determine the types of the sortable values.
  switch (propMap[prop]) {
    case 'duration':
      left = left as moment.Duration;
      right = right as moment.Duration;

      return left.asMilliseconds() - right.asMilliseconds();

    case 'string':
      left = left as string;
      right = right as string;

      return left.toLowerCase().localeCompare(right.toLowerCase());

    case 'timestamp':
      // Timestamp values are expected to be strings parsable as UTC dates.
      left = left as string;
      right = right as string;

      // Lazily cache left value as Moment.
      const leftCached: boolean = timestampCache.has(left);
      const leftMoment: moment.Moment = leftCached
        ? timestampCache.get(left)!
        : moment.utc(left, 'YYYY-MM-DD HH:mm:ss');
      if (!leftCached) {
        timestampCache.set(left, leftMoment);
      }

      // Lazily cache right value as Moment.
      const rightCached: boolean = timestampCache.has(right);
      const rightMoment: moment.Moment = rightCached
        ? timestampCache.get(right)!
        : moment.utc(right, 'YYYY-MM-DD HH:mm:ss');
      if (!rightCached) {
        timestampCache.set(right, rightMoment);
      }

      return leftMoment.diff(rightMoment);

    default:
      // The values are expected to be numeric.
      return (left as number) - (right as number);
  }
}

export function sort(
  data: any[],
  sorts: SortDirection[],
  propMap: PropMapType
) {
  // Record the sort order which may be set by previous sort operations.
  // This is necessary for stable sorting.
  const indexMap = new Map<any, number>();
  data.forEach((item, index) => indexMap.set(item, index));

  // Cache converted timestamp values within the context of the sort.
  const timestampCache: TimestampCacheType = new Map<string, moment.Moment>();

  return data.sort((a, b) => {
    for (const sort of sorts) {
      const { direction, prop } = sort;
      const isAsc = direction === SortDir.Asc;
      const left = isAsc ? a[prop] : b[prop];
      const right = isAsc ? b[prop] : a[prop];
      const comparison = getComparison(
        left,
        right,
        prop,
        propMap,
        timestampCache
      );

      // Do not return 0 as there may be other properties to sort.
      if (comparison !== 0) {
        return comparison;
      }
    }

    // Determine if the original order exists in map.
    if (!indexMap.has(a) || !indexMap.has(b)) {
      return 0;
    }

    // Preserve the original order to maintain a stable sort. Each value is
    // expected to exist.
    return indexMap.get(a)! < indexMap.get(b)! ? -1 : 1;
  });
}
